import { useMemo, useState } from 'react';
import { allPosts, categories, categoryMap, formatReads } from '@/mocks/gossip';

export default function Feed() {
  const [active, setActive] = useState('all');
  const [kw, setKw] = useState('');
  const [openIds, setOpenIds] = useState<string[]>([]);

  const list = useMemo(() => {
    const key = kw.trim().toLowerCase();
    return allPosts.filter((p) => {
      const inCat = active === 'all' || p.cat === active;
      if (!inCat) return false;
      if (!key) return true;
      const catName = categoryMap[p.cat]?.name ?? '';
      return (
        p.title.toLowerCase().includes(key) ||
        p.brief.toLowerCase().includes(key) ||
        p.tags.join('').toLowerCase().includes(key) ||
        catName.toLowerCase().includes(key)
      );
    });
  }, [active, kw]);

  const toggle = (id: string) =>
    setOpenIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const chipBase =
    'whitespace-nowrap inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors';
  const chipOn = 'border-primary-500 bg-primary-500 text-background-50';
  const chipOff =
    'border-background-200 bg-background-50 text-foreground-700 hover:border-secondary-300 hover:text-primary-600';

  return (
    <section id="pan" className="relative w-full bg-background-50 py-16 md:py-20">
      <div className="mx-auto max-w-[1240px] px-4 md:px-6">
        {/* 栏目标题 */}
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-primary-500">
              <i className="ri-list-unordered text-base"></i>
              PICK &amp; PICK
            </p>
            <h2 className="rule-line font-display text-3xl font-black text-foreground-950 md:text-4xl">
              分类盘点：把瓜按栏目切好
            </h2>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-foreground-700">
              红毯翻车、造型雷人、穿搭翻车、撞衫对比、妆容失败、时尚灾难——六大栏目各有侧重。
              想围观哪一类，点一下就只给你上这类瓜。
            </p>
          </div>

          {/* 搜索 */}
          <div className="w-full md:w-72">
            <label
              htmlFor="gua-search"
              className="mb-2 block text-xs font-medium text-foreground-600"
            >
              站内搜瓜
            </label>
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-foreground-50 px-3 py-2.5">
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                id="gua-search"
                type="text"
                value={kw}
                onChange={(e) => setKw(e.target.value)}
                placeholder="搜标题、关键词，如「垫肩」"
                className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
              {kw && (
                <button
                  type="button"
                  aria-label="清空搜索"
                  onClick={() => setKw('')}
                  className="text-foreground-400 hover:text-primary-600"
                >
                  <i className="ri-close-circle-fill text-base"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 分类标签 */}
        <div className="no-scrollbar mt-8 flex gap-2 overflow-x-auto pb-1">
          <button type="button" onClick={() => setActive('all')} className={`${chipBase} ${active === 'all' ? chipOn : chipOff}`}>
            <i className="ri-layout-grid-line text-sm"></i>
            全部栏目
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActive(c.id)}
              className={`${chipBase} ${active === c.id ? chipOn : chipOff}`}
            >
              <i className={`${c.icon} text-sm`}></i>
              {c.name}
            </button>
          ))}
        </div>

        {/* 结果统计 */}
        <p className="mt-6 text-xs text-foreground-500">
          当前栏目
          <span className="mx-1 font-bold text-primary-600">{active === 'all' ? '全部瓜田' : categoryMap[active]?.name}</span>
          共 <span className="font-bold text-primary-600">{list.length}</span> 条，持续上新
        </p>

        {/* 卡片网格 */}
        {list.length > 0 ? (
          <div className="mt-7 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((p) => {
              const isOpen = openIds.includes(p.id);
              return (
                <article
                  key={p.id}
                  className="group flex flex-col overflow-hidden rounded-xl border border-background-200/80 bg-foreground-50"
                >
                  <div className="relative h-52 w-full overflow-hidden">
                    <img
                      src={p.img}
                      alt={p.title}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-foreground-950/70 px-3 py-1 text-xs font-medium text-background-50 backdrop-blur-sm">
                      <i className={`${categoryMap[p.cat]?.icon} text-accent-300`}></i>
                      {categoryMap[p.cat]?.name}
                    </span>
                    <span className="absolute right-3 top-3 rounded-md bg-primary-500 px-2 py-1 text-xs font-semibold text-background-50">
                      {p.edition}
                    </span>
                  </div>

                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-display text-lg font-bold leading-snug text-foreground-950">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-600">{p.brief}</p>

                    {/* 小编开麦展开区 */}
                    {isOpen && (
                      <div className="mt-4 rounded-lg border-l-[3px] border-accent-400 bg-accent-50 px-4 py-3">
                        <p className="text-xs font-semibold text-accent-700">
                          <i className="ri-chat-quote-line mr-1"></i>小编毒舌点评
                        </p>
                        <p className="mt-1.5 text-sm leading-relaxed text-foreground-800">{p.punch}</p>
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap items-center gap-1.5">
                      {p.tags.map((t) => (
                        <span
                          key={t}
                          className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-900"
                        >
                          #{t}
                        </span>
                      ))}
                    </div>

                    <div className="mt-auto flex items-center justify-between border-t border-background-200/70 pt-4">
                      <span className="text-xs text-foreground-500">{p.edition}</span>
                      <span className="flex items-center gap-3 text-xs text-foreground-500">
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-eye-line text-secondary-600"></i>
                          {formatReads(p.reads)}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-chat-3-line text-secondary-600"></i>
                          {p.comments}
                        </span>
                        <button
                          type="button"
                          onClick={() => toggle(p.id)}
                          className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
                            isOpen
                              ? 'bg-secondary-200 text-secondary-900'
                              : 'bg-background-100 text-foreground-700 hover:bg-accent-100 hover:text-accent-700'
                          }`}
                        >
                          <i className={`${isOpen ? 'ri-arrow-up-s-line' : 'ri-chat-smile-3-line'} text-sm`}></i>
                          {isOpen ? '收起' : '小编开麦'}
                        </button>
                      </span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="mt-8 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-foreground-50 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-information-line"></i>
            </span>
            <p className="mt-4 font-display text-lg font-bold text-foreground-800">
              这片瓜田暂时没有你要的瓜
            </p>
            <p className="mt-1 text-sm text-foreground-500">换个栏目或换个关键词再试试，吃瓜不着急。</p>
            <button
              type="button"
              onClick={() => {
                setActive('all');
                setKw('');
              }}
              className="mt-5 inline-flex items-center whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line mr-1.5 text-base"></i>
              回到全部
            </button>
          </div>
        )}
      </div>
    </section>
  );
}